import * as React from "react";
import { 
    List, Show, Edit, Create, 
    Datagrid, Filter, SimpleList, SimpleShowLayout, SimpleForm, Pagination,
    TextField, DateField, BooleanField, FunctionField,
    TextInput, DateInput, BooleanInput, RadioButtonGroupInput,
    required, useNotify, useRedirect, useShowController
} from 'react-admin';
import { Link, useMediaQuery } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import LogoField from '../components/LogoField';
import DateTimeField from '../components/DateTimeField';

const useStyles = makeStyles(
    {
        tertiary: {
            opacity: 1
        }
    },
    {
        name: 'RaSimpleList'
    }
);

const PlayerFilter = props => (
    <Filter {...props}>
        <TextInput label="Search" source="searchText" alwaysOn />
        <RadioButtonGroupInput label="Status" source="isActive" choices={[
            { id: null, name: 'All' },
            { id: true, name: 'Active' },
            { id: false, name: 'Inactive' }
        ]} alwaysOn />
    </Filter>
);

export const PlayerList = props => {
    const isSmall = useMediaQuery(theme => theme.breakpoints.down('sm'));
    const classes = useStyles(props);

    const ListPagination = (props) => <Pagination rowsPerPageOptions={[10, 25, 50, 100]} {...props} />;

    return (
        <List {...props} bulkActionButtons={false} filters={<PlayerFilter />} filterDefaultValues={{ isActive: null }} pagination={<ListPagination />} perPage={10}>
            {isSmall ? (
                <SimpleList classes={classes}
                    primaryText={record => `${record.lastName}, ${record.firstName}`}
                    secondaryText={record => record.age ? record.age : 'Unknown DOB'}
                    tertiaryText={record => <LogoField type="players" source="id" extension="png" fallback="player.png" height={42} />}
                    linkType="show"
                />
            ) : (
                <Datagrid rowClick="show">
                    <LogoField type="players" source="id" text="firstName" extension="png" fallback="player.png" height={48} width={58} label="First Name" />
                    <TextField source="lastName" label="Last Name" />
                    <DateField source="dob" label="Date Of Birth" options={{ day: 'numeric', month: 'long', year: 'numeric' }} locales="en-AU" />
                    <TextField source="age" label="Age" />
                    <BooleanField source="isActive" label="Active?" />
                </Datagrid>
            )}
        </List>
    );
};
export const PlayerShow = props => {
    const { loading } = useShowController(props);
    if (loading) return null;

    return (
        <Show {...props}>
            <SimpleShowLayout>
                <LogoField type="players" source="id" extension="png" fallback="player.png" height={192} addLabel={false} />
                <TextField source="id" label="ID" />
                <TextField source="firstName" label="First Name" />
                <TextField source="lastName" label="Last Name" />
                <DateField source="dob" label="Date Of Birth" options={{ day: 'numeric', month: 'long', year: 'numeric' }} locales="en-AU" />
                <FunctionField render={record => <Link href={`https://www.foxsports.com.au/nrl/player?playerid=${record.externalId}`} target="_blank" rel="noreferrer">{record.externalId}</Link>} label="External ID" />
                <BooleanField source="isActive" label="Active?" />
                <DateTimeField source="createdUtc" label="Created" />
                <DateTimeField source="lastUpdatedUtc" label="Last Updated" />
            </SimpleShowLayout>
        </Show>
    );
};

export const PlayerEdit = props => (
    <Edit {...props} mutationMode="optimistic">
        <SimpleForm>
            <TextInput source="id" label="ID" fullWidth={true} disabled />
            <TextInput source="firstName" label="First Name" fullWidth={true} validate={[required()]} />
            <TextInput source="lastName" label="Last Name" fullWidth={true} validate={[required()]} />
            <DateInput source="dob" label="Date of Birth" />
            <TextInput source="externalId" label="External ID" fullWidth={true} />
            <BooleanInput source="isActive" label="Active?" validate={[required()]} />
        </SimpleForm>
    </Edit>
);

export const PlayerCreate = props => {
    const notify = useNotify();
    const redirect = useRedirect();

    const onSuccess = ({ data }) => {
		notify(`Player '${data.firstName} ${data.lastName}' successfully created.`);
		redirect(`/player/${data.id}/show`);
	};

    return (
        <Create onSuccess={onSuccess} {...props}>
            <SimpleForm>
                <TextInput source="firstName" label="First Name" fullWidth={true} validate={[required()]} />
                <TextInput source="lastName" label="Last Name" fullWidth={true} validate={[required()]} />
                <DateInput source="dob" label="Date of Birth" />
                <TextInput source="externalId" label="External ID" fullWidth={true} />
            </SimpleForm>
        </Create>
    );
};